import React, { useCallback, useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import AccessTime from "@mui/icons-material/AccessTime";
import AddLink from "@mui/icons-material/AddLink";
import AddRounded from "@mui/icons-material/AddRounded";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import ModeStandby from "@mui/icons-material/ModeStandby";
import People from "@mui/icons-material/People";
import SocialDistance from "@mui/icons-material/SocialDistance";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import {
  checkEditorPermission,
  subModuleNameMatches,
} from "services/utils/HelperFunction";
import {
  designShell,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import {
  sharedStickyCustomFilterBarSx,
  sharedStickyFooterBarSx,
} from "components/DesignSystem/sharedUiPatterns";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { Loader } from "components/Loader";
import { FilterProvider } from "pages/FilterContext";
import { ApproveTable } from "pages/TeamZone/Approve/approveUi";
import {
  balancedColumnWidth,
  monitoringBodyCellSx,
  monitoringHeadCellSx,
  monitoringNameSx,
  monitoringRowSx,
  useMeasuredHeight,
} from "pages/Monitoring/listUi";
import { showToast } from "services/utils/Status";
import { PolicyPanel } from "./Policy";
import { resolveSettingsAccess } from "../settingsAccess";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { NO_COLOR, YES_COLOR, useFilteredRows } from "../settingsUi";

// Standard list headers: drive the table columns, the CustomFilter toolbar
// (search / sort / filter / download) and the "Default View" column picker.
// A row opens its policy in the side panel; there is no Action column.
const policyHeaders = [
  { name: "Scope", key: "scope_model", type: "autocomplete", icon: <ModeStandby fontSize="small" /> },
  { name: "Name", key: "scope_name", type: "autocomplete", icon: <People fontSize="small" /> },
  { name: "Checkpoint Threshold (min)", key: "threshold_minutes", type: "number", icon: <AccessTime fontSize="small" /> },
  { name: "Trip Distance Threshold (m)", key: "trip_distance_threshold", type: "number", icon: <SocialDistance fontSize="small" /> },
  { name: "Allow check-in from anywhere", key: "allow_anywhere_checkin_checkout", type: "boolean", icon: <AutoAwesome fontSize="small" /> },
  { name: "Work Session Automation", key: "is_linked_to_checkin", type: "boolean", icon: <AddLink fontSize="small" /> },
];

// Fixed-layout widths; "Name" takes whatever is left so the table always
// fits its card.
const columnWidths = {
  scope_model: "13%",
  threshold_minutes: "17%",
  trip_distance_threshold: "17%",
  allow_anywhere_checkin_checkout: "18%",
  is_linked_to_checkin: "16%",
};

const BOOLEAN_KEYS = ["allow_anywhere_checkin_checkout", "is_linked_to_checkin"];

const capitalise = (text) =>
  text ? String(text).charAt(0).toUpperCase() + String(text).slice(1) : "";

export const MonitoringPolicy = ({ moduleDetails: moduleDetailsProp }) => {
  const { domain, userScreenAccess, topbarCompanyId, userDetails } =
    useSelector((state) => state.tokenReducer);
  // Opened from Team Workspace > Settings, where Redux holds Team Workspace's
  // provisioning payload rather than the one that owns this screen's
  // permissions; the page passes the right module list in (see
  // TeamZone/Settings/settingsAccess.js).
  const moduleDetails =
    moduleDetailsProp ?? userScreenAccess?.module_details;
  // People's real provisioning payload names this module "Settings", not
  // "Feature Configuration" (see Sidebar/index.js's PEOPLE_VIEWER_GATED_MODULES
  // comment). Its submodule is provisioned as "Check In Policy" (capital
  // I) — subModuleNameMatches applies the same alias table
  // checkEditorPermission uses internally, so this stays in sync with it.
  const FeatureConfig = moduleDetails?.find(
    (mod) => mod.module_name === "Settings",
  );

  const subModuleId = FeatureConfig?.submodules?.find((sub) =>
    subModuleNameMatches(sub.sub_module_name, "Check in Policy"),
  )?.sub_module_id;

  const check_access = checkEditorPermission(
    moduleDetails,
    "Settings",
    "Check in Policy",
  );

  const {
    result: policyData,
    setLoad: reCallApi,
    loading,
  } = useFetch(`${domain}tracking/trip-monitoring-policies/`);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  // null (closed), { policy: null } (create) or { policy: row } (edit).
  const [panel, setPanel] = useState(null);
  const [customFilterRef, customFilterHeight] = useMeasuredHeight();
  const [footerRef, footerHeight] = useMeasuredHeight();
  const resetPage = useCallback(() => setPage(0), []);

  const policies = useMemo(
    () => (Array.isArray(policyData) ? policyData : []),
    [policyData],
  );

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "team_settings_checkin_policy_saved_views",
    fullHeaders: policyHeaders,
    screenAccess: Boolean(subModuleId),
    onViewChange: resetPage,
  });

  useEffect(() => {
    if (topbarCompanyId) {
      reCallApi(true);
    }
  }, [reCallApi, topbarCompanyId]);

  // The rows after the toolbar's search, filters and sort (see
  // useFilteredRows for why a stale result is never shown).
  const {
    rows,
    ready: filteredReady,
    onFilteredDataChange,
  } = useFilteredRows(policies);
  const safePage = Math.min(
    page,
    Math.max(0, Math.ceil(rows.length / rowsPerPage) - 1),
  );
  const pageStart = safePage * rowsPerPage;
  // Editor creates and updates a policy; approver deletes it.
  const { canDelete } = resolveSettingsAccess(moduleDetails, "Check in Policy");
  const openId = panel?.policy?.id;

  const openCreate = () => {
    if (check_access) {
      setPanel({ policy: null });
    } else {
      showToast(
        "error",
        "Access denied: You are not authorized to create a policy",
      );
    }
  };

  const renderCell = (key, policy) => {
    // Yes / No shown as a status chip.
    if (BOOLEAN_KEYS.includes(key)) {
      return (
        <RequestStatusChip
          colorCode={policy[key] ? YES_COLOR : NO_COLOR}
          status={policy[key] ? "Yes" : "No"}
        />
      );
    }
    if (key === "scope_model") return capitalise(policy.scope_model) || "N/A";
    if (key === "scope_name") {
      return (
        <Mui.Typography noWrap sx={monitoringNameSx(openId === policy.id)}>
          {policy.scope_name ?? "N/A"}
        </Mui.Typography>
      );
    }
    return policy[key] ?? "N/A";
  };

  // Only the first load replaces the table; a refresh after a save keeps the
  // current rows on screen.
  const firstLoad = loading && policies.length === 0;

  return (
    <FilterProvider
      domain={domain}
      userDetails={userDetails}
      subModuleId={subModuleId}
    >
      <Mui.Box
        ref={customFilterRef}
        sx={{ ...designShell.sectionCard, ...sharedStickyCustomFilterBarSx }}
      >
        <CustomFilter
          title="Records"
          titleOverride={titleOverride}
          filterHeaders={visibleHeaders}
          data={policies}
          filteredData={rows}
          filteredDataReady={filteredReady}
          onFilteredDataChange={onFilteredDataChange}
          screenAccess={check_access}
          hideCount={true}
          labeledToolbar={true}
          downloadFormats={["excel", "pdf", "csv"]}
          searchPlaceholder="Search policies by scope or name..."
          newButtonComponent={check_access &&
            <PrimaryButton
              startIcon={<AddRounded />}
              onClick={openCreate}
              sx={{ minWidth: 0, minHeight: 40, whiteSpace: "nowrap", flexShrink: 0 }}
            >
              New
            </PrimaryButton>
          }
        />
      </Mui.Box>

      <Mui.Box sx={{ ...designShell.sectionCard, width: "100%" }}>
        {firstLoad ? (
          <Mui.Box sx={{ py: 6 }}>
            <Loader info="Loading..." />
          </Mui.Box>
        ) : (
          <>
            <Mui.TableContainer
              sx={{
                width: "100%",
                // Visible, not auto: the sticky header needs the page as its
                // scroll parent, and the fixed-layout table always fits.
                overflowX: "visible",
                pb: footerHeight ? footerHeight + "px" : 0,
              }}
            >
              <ApproveTable
                stickyHeader
                aria-label="Check-in policies"
                sx={{ width: "100%", tableLayout: "fixed" }}
              >
                <Mui.TableHead sx={designShell.tableStickyHead}>
                  <Mui.TableRow sx={designShell.tableStickyHeadRow}>
                    {visibleHeaders.map((header) => (
                      <Mui.TableCell
                        key={header.key}
                        scope="col"
                        title={header.name}
                        sx={{
                          ...monitoringHeadCellSx(customFilterHeight),
                          width: balancedColumnWidth(header.key, visibleHeaders, policyHeaders, columnWidths),
                        }}
                      >
                        {header.name}
                      </Mui.TableCell>
                    ))}
                  </Mui.TableRow>
                </Mui.TableHead>
                <Mui.TableBody>
                  {rows.length === 0 ? (
                    <TableEmptyState
                      dataLength={policies}
                      colSpan={visibleHeaders.length}
                    />
                  ) : (
                    rows
                      .slice(pageStart, pageStart + rowsPerPage)
                      .map((policy) => (
                        <Mui.TableRow
                          key={policy.id}
                          onClick={() => setPanel({ policy })}
                          sx={monitoringRowSx(openId === policy.id)}
                        >
                          {visibleHeaders.map((header) => (
                            <Mui.TableCell
                              key={header.key}
                              sx={monitoringBodyCellSx}
                            >
                              {renderCell(header.key, policy)}
                            </Mui.TableCell>
                          ))}
                        </Mui.TableRow>
                      ))
                  )}
                </Mui.TableBody>
              </ApproveTable>
            </Mui.TableContainer>
            <Mui.Box ref={footerRef} sx={sharedStickyFooterBarSx}>
              {paginationFooter({
                page: safePage,
                rowsPerPage,
                total: rows.length,
                itemLabel: "policies",
                rowsPerPageOptions,
                onPageChange: setPage,
                onRowsPerPageChange: (event) => {
                  setRowsPerPage(Number(event.target.value));
                  setPage(0);
                },
              })}
            </Mui.Box>
          </>
        )}
      </Mui.Box>

      <PolicyPanel
        open={Boolean(panel)}
        policy={panel?.policy ?? null}
        canEdit={check_access}
        canDelete={canDelete}
        onClose={() => setPanel(null)}
        onChanged={() => reCallApi(true)}
      />
    </FilterProvider>
  );
};
